// @ts-strict-ignore
import { Box } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { analyticsMessages } from "../../messages";
import { SalesPoint } from "../../models/analytics";
import { EmptyState } from "../EmptyState";
import { surfaceCardStyle } from "./tokens";
import { TrendBadge } from "./TrendBadge";

interface ChartTileV2Props {
  title: string;
  data: SalesPoint[];
  previousData?: SalesPoint[];
  loading?: boolean;
  na?: boolean;
  emptyMessage?: string;
  totalLabel?: string;
  totalValue?: string;
  delta?: number | null;
  currency?: string;
  height?: number;
  color?: string;
}

const formatShortDate = (s: string) => {
  if (!s) return "";

  const d = new Date(s + "T00:00:00Z");

  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const formatCurrencyShort = (v: number, currency?: string) => {
  const abs = Math.abs(v);
  let val = v;
  let suffix = "";

  if (abs >= 1_000_000) {
    val = v / 1_000_000;
    suffix = "M";
  } else if (abs >= 1_000) {
    val = v / 1_000;
    suffix = "k";
  }

  const num = val.toLocaleString(undefined, { maximumFractionDigits: abs >= 1000 ? 1 : 0 });

  return currency ? `${currency} ${num}${suffix}` : `${num}${suffix}`;
};

const CustomTooltip: React.FC<any> = ({ active, payload, label, currency }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <Box
      padding={3}
      borderRadius={3}
      style={{
        background: "rgba(17, 24, 39, 0.95)",
        color: "#fff",
        boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
        fontSize: 12,
        minWidth: 140,
      }}
    >
      <Box style={{ opacity: 0.7, marginBottom: 4 }}>{formatShortDate(label)}</Box>
      {payload.map((p: any) => (
        <Box
          key={p.dataKey}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={3}
          style={{ marginTop: 2 }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              as="span"
              __width={8}
              __height={8}
              borderRadius={1}
              style={{ background: p.color }}
            />
            <span style={{ opacity: 0.8 }}>{p.name}</span>
          </Box>
          <span style={{ fontWeight: 600 }}>
            {formatCurrencyShort(p.value, currency)}
          </span>
        </Box>
      ))}
    </Box>
  );
};

export const ChartTile: React.FC<ChartTileV2Props> = ({
  title,
  data,
  previousData,
  loading,
  na,
  emptyMessage,
  totalLabel,
  totalValue,
  delta,
  currency,
  height = 280,
  color = "#4F46E5",
}) => {
  const intl = useIntl();

  const series = React.useMemo(() => {
    if (!data) return [];

    if (!previousData || previousData.length === 0) {
      return data.map(d => ({ date: d.date, current: d.value }));
    }

    const len = Math.max(data.length, previousData.length);
    const out: Array<{ date: string; current?: number; previous?: number }> = [];

    for (let i = 0; i < len; i++) {
      out.push({
        date: data[i]?.date ?? previousData[i]?.date ?? String(i),
        current: data[i]?.value,
        previous: previousData[i]?.value,
      });
    }

    return out;
  }, [data, previousData]);

  const empty = !na && !loading && (!data || data.length === 0);

  return (
    <Box padding={5} style={{ ...surfaceCardStyle }}>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={3} marginBottom={4}>
        <Box>
          <Box as="div" fontSize={2} color="default2">
            {title}
          </Box>
          {(totalValue || totalLabel) && (
            <Box display="flex" alignItems="center" gap={3} marginTop={2}>
              {totalValue && (
                <Box
                  as="div"
                  fontSize={7}
                  fontWeight="bold"
                  style={{ letterSpacing: "-0.02em", fontFeatureSettings: '"tnum"' }}
                >
                  {totalValue}
                </Box>
              )}
              {delta != null && <TrendBadge delta={delta} />}
            </Box>
          )}
        </Box>
        {previousData && previousData.length > 0 && (
          <Box display="flex" alignItems="center" gap={3} fontSize={1} color="default2">
            <Box display="inline-flex" alignItems="center" gap={1}>
              <Box as="span" __width={10} __height={2} style={{ background: color, borderRadius: 2 }} />
              Current
            </Box>
            <Box display="inline-flex" alignItems="center" gap={1}>
              <Box
                as="span"
                __width={10}
                __height={2}
                style={{
                  borderTop: "2px dashed #9CA3AF",
                  display: "inline-block",
                }}
              />
              Previous
            </Box>
          </Box>
        )}
      </Box>

      <Box display="flex" alignItems="center" justifyContent="center" color="default2" style={{ height }}>
        {na ? (
          intl.formatMessage(analyticsMessages.commonNA)
        ) : loading ? (
          intl.formatMessage(analyticsMessages.commonLoading)
        ) : empty ? (
          <EmptyState message={emptyMessage ?? intl.formatMessage(analyticsMessages.commonNoData)} compact />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 12, right: 12, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="salesV2Color" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(15, 23, 42, 0.06)" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: "#6B7280" }}
                tickLine={false}
                axisLine={{ stroke: "rgba(15, 23, 42, 0.08)" }}
                tickFormatter={formatShortDate}
                minTickGap={24}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#6B7280" }}
                tickLine={false}
                axisLine={false}
                tickFormatter={v => formatCurrencyShort(v, currency)}
                width={56}
              />
              <Tooltip content={<CustomTooltip currency={currency} />} cursor={{ stroke: "rgba(15, 23, 42, 0.12)", strokeWidth: 1 }} />
              {previousData && previousData.length > 0 && (
                <Line
                  type="monotone"
                  dataKey="previous"
                  name="Previous period"
                  stroke="#9CA3AF"
                  strokeDasharray="4 4"
                  strokeWidth={1.5}
                  dot={false}
                />
              )}
              <Area
                type="monotone"
                dataKey="current"
                name="Current period"
                stroke={color}
                fill="url(#salesV2Color)"
                strokeWidth={2.25}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 2, stroke: "#fff" }}
              />
              {previousData && previousData.length > 0 && <Legend wrapperStyle={{ display: "none" }} />}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Box>
  );
};
